import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronDown, 
  Building2,
  Briefcase,
  Heart,
  Scale,
  Factory,
  Building,
  GraduationCap,
  Home,
  Microscope,
  Landmark,
  BookOpen,
  FlaskConical
} from 'lucide-react';

// Industry icon mapping
const industryIconMap = {
  'Cross Industry Functions': Briefcase,
  'Healthcare and Hospital': Heart,
  'Banking Financial Services and Insurance': Landmark,
  'Manufacturing': Factory,
  'Construction': Building,
  'Legal and Law': Scale,
  'Real Estate': Home,
  'Other Trainings': BookOpen,
  'Generic Webinars': GraduationCap,
  'Life Sciences': FlaskConical,
  'default': Building2
};

const getIndustryIcon = (industryName) => {
  return industryIconMap[industryName] || industryIconMap.default;
};

/**
 * IndustryMegaMenu - Dropdown with parent industries on left, sub-industries on right
 * Similar to Udemy category navigation
 */
export const IndustryMegaMenu = ({ 
  isScrolled = false, 
  isHomePage = false,
  onNavigate = () => {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [hoveredParent, setHoveredParent] = useState(null);
  const [loading, setLoading] = useState(true);
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetchIndustries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setHoveredParent(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchIndustries = async () => {
    try {
      // Use the hierarchy endpoint which returns parent-child structure
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/industries/hierarchy`);
      const data = await response.json();
      
      // data is already in the format: [{id, name, slug, is_parent, children: [...]}]
      const sortedParents = Array.isArray(data) ? data : [];
      
      setIndustries(sortedParents);
      if (sortedParents.length > 0) {
        setHoveredParent(sortedParents[0].id);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const handleParentHover = (parentId) => {
    setHoveredParent(parentId);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setHoveredParent(null);
    onNavigate();
  };

  const currentParent = industries.find(ind => ind.id === hoveredParent);

  return (
    <div 
      ref={menuRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger Button */}
      <button
        className={`flex items-center gap-1 px-4 py-2.5 rounded-xl font-medium text-[15px] transition-all duration-300 ${
          isOpen
            ? true
              ? 'text-navy-700 bg-navy-50'
              : 'text-white bg-white/15'
            : true
            ? 'text-slate-600 hover:text-navy-700 hover:bg-slate-50'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        }`}
        data-testid="industries-dropdown"
      >
        Industries
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50"
          style={{ minWidth: '600px' }}
        >
          <div className="flex">
            {/* Left Panel - Parent Industries */}
            <div className="w-64 bg-slate-50 border-r border-slate-200 py-2 max-h-[450px] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-slate-500">Loading...</div>
              ) : (
                industries.map((parent) => {
                  const parentId = parent.id;
                  const isActive = hoveredParent === parentId;
                  const hasChildren = parent.children && parent.children.length > 0;
                  const IconComponent = getIndustryIcon(parent.name);
                  
                  return (
                    <div
                      key={parentId}
                      onMouseEnter={() => handleParentHover(parentId)}
                      className={`group cursor-pointer ${isActive ? 'bg-white' : ''}`}
                    >
                      <Link
                        to={`/industries/${parent.slug || parentId}`}
                        onClick={handleLinkClick}
                        className={`flex items-center gap-3 px-4 py-3 transition-all ${
                          isActive 
                            ? 'text-navy-700 font-medium' 
                            : 'text-slate-700 hover:text-navy-700'
                        }`}
                      >
                        <IconComponent className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-gold-500' : 'text-slate-400'}`} />
                        <span className="text-sm flex-1">{parent.name}</span>
                        {hasChildren && (
                          <ChevronRight className={`w-4 h-4 ${isActive ? 'text-gold-500' : 'text-slate-400'}`} />
                        )}
                      </Link>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right Panel - Sub Industries */}
            <div className="flex-1 p-4 max-h-[450px] overflow-y-auto">
              {currentParent && (
                <>
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gold-600 uppercase tracking-wider mb-1">
                      Popular Topics
                    </h3>
                    <Link
                      to={`/industries/${currentParent.slug || currentParent.id}`}
                      onClick={handleLinkClick}
                      className="text-lg font-bold text-navy-700 hover:text-gold-600 transition-colors"
                    >
                      {currentParent.name}
                    </Link>
                  </div>

                  {currentParent.children && currentParent.children.length > 0 ? (
                    <div className="grid grid-cols-1 gap-1">
                      {currentParent.children.map((child) => (
                        <Link
                          key={child.id}
                          to={`/industries/${child.slug || child.id}`}
                          onClick={handleLinkClick}
                          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-slate-700 hover:bg-gold-50 hover:text-navy-700 transition-all group"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-gold-500 transition-colors" />
                          <span className="text-sm">{child.name}</span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      Explore all {currentParent.name} training programs
                    </p>
                  )}

                  {/* View All Link */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <Link
                      to={`/industries/${currentParent.slug || currentParent.id}`}
                      onClick={handleLinkClick}
                      className="inline-flex items-center gap-2 text-sm font-medium text-navy-600 hover:text-gold-600 transition-colors"
                    >
                      <Building2 className="w-4 h-4" />
                      View All {currentParent.name} Webinars
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer - View All Industries */}
          <div className="bg-zinc-900 px-4 py-3">
            <Link
              to="/industries"
              onClick={handleLinkClick}
              className="flex items-center justify-between text-white hover:text-gold-300 transition-colors"
            >
              <span className="text-sm font-medium">Browse All Industries</span>
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustryMegaMenu;
